import { PageHero, CTASection } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";
import { Quote, Telescope, Expand, Sparkles } from "lucide-react";

export default function TheCreator() {
  return (
    <div data-testid="thecreator-page">
      <PageHero
        label="THECREATOR"
        lines={["Why", "\u201CTHECREATOR\u201D?"]}
        intro="Our name is not a slogan — it is a conviction. The deeper science looks into the origin of space, time, matter and energy, the more clearly the Universe points beyond itself."
      />

      {/* A · The beginning of cosmic time */}
      <section className="border-t border-white/10" data-testid="creator-origin">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="A">The Beginning of Cosmic Time</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Every effect has a cause — <span className="text-accent">including the Universe itself</span>.
          </h2>

          <div className="grid lg:grid-cols-12 gap-10 mt-14 items-start">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="w-full aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black" data-testid="creator-video">
                  <YouTubeEmbed id="GHXyH2RWHyk" title="The origin of the Universe — space, time, matter and energy" />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.08}>
                <div className="rounded-3xl border dk-border dk-panel p-8 md:p-10 relative overflow-hidden" data-testid="hugh-ross-quote">
                  <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-20" style={{ background: "#2AA7D4" }} />
                  <Quote className="w-8 h-8 text-c-amber" />
                  <blockquote className="font-display italic text-lg md:text-xl dk-text leading-relaxed mt-5">
                    “Time is the dimension in which cause-and-effect relationships occur. Effects follow their causes. So the beginning of cosmic time implies that an Agent (cause) outside the universe's space-time dimensions is responsible for bringing into existence the space, time, matter, and energy (effects) astronomers observe.”
                  </blockquote>
                  <div className="mt-6 pt-5 border-t dk-border">
                    <div className="font-display text-lg dk-text tracking-tight">Prof. Hugh Ross</div>
                    <div className="font-mono-label text-[10px] tracking-label uppercase dk-text-muted mt-1.5">
                      Astrophysicist · “More Than a Theory”, pp. 97–98
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* B · Einstein's Cosmological Equation */}
      <section className="border-t border-white/10" data-testid="creator-einstein">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="B">Einstein's Cosmological Equation</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            One equation — gravity, geometry, matter, energy and <span className="text-accent">the evolution of the Universe</span>.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            At the heart of Einstein's theory of General Relativity is the field equation — often regarded as one of the most beautiful equations in science.
          </p>

          <Reveal>
            <div className="mt-12 rounded-3xl border dk-border dk-panel p-10 md:p-16 relative overflow-hidden" data-testid="field-equation-panel">
              <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full blur-3xl opacity-20" style={{ background: "#F0A13C" }} />
              <div className="absolute -bottom-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-20" style={{ background: "#2AA7D4" }} />
              <div className="relative text-center">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">The Einstein Field Equation</div>
                <div className="inline-block mt-8 px-8 md:px-14 py-6 md:py-8 rounded-2xl border-2" style={{ borderColor: "rgba(42,167,212,0.5)", backgroundColor: "rgba(255,255,255,0.04)" }}>
                  <span className="font-display text-3xl md:text-5xl dk-text tracking-tight whitespace-nowrap" data-testid="field-equation">
                    G<sub className="text-[0.55em]">μν</sub> + Λg<sub className="text-[0.55em]">μν</sub> ={" "}
                    <span className="inline-flex flex-col align-middle text-[0.72em] leading-tight mx-1">
                      <span className="border-b-2 px-2" style={{ borderColor: "rgba(255,255,255,0.7)" }}>8πG</span>
                      <span className="px-2">c⁴</span>
                    </span>{" "}
                    T<sub className="text-[0.55em]">μν</sub>
                  </span>
                </div>
                <p className="font-display italic text-xl md:text-2xl dk-text-soft leading-relaxed mt-10 max-w-3xl mx-auto">
                  “Matter and energy tell space-time how to curve, and curved space-time tells matter how to move.”
                </p>
                <p className="dk-text-muted text-sm leading-relaxed mt-6 max-w-2xl mx-auto">
                  It links gravity, geometry, matter, energy, and the evolution of the Universe in a single mathematical relationship.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* C · Expansion of the Universe */}
      <section className="border-t border-white/10" data-testid="creator-expansion">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="C">Expansion of the Universe</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Einstein's equations predict that the Universe <span className="text-accent">cannot remain static</span>.
          </h2>

          <div className="grid sm:grid-cols-3 gap-6 mt-14">
            {[
              { icon: Expand, t: "Expanding Universe", d: "Space itself stretches, carrying galaxies apart — the solution our observations confirm.", hex: "#2AA7D4" },
              { icon: Telescope, t: "Contracting Universe", d: "A universe collapsing under its own gravity — mathematically possible, but not what we observe.", hex: "#B79CF5" },
              { icon: Sparkles, t: "Accelerating Universe", d: "Not only is our Universe expanding — observations show that expansion is currently accelerating.", hex: "#F0A13C" },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.t} delay={i * 0.08} className="border border-white/10 rounded-2xl bg-[#070d18] p-8" data-testid={`expansion-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center border" style={{ borderColor: c.hex, backgroundColor: `${c.hex}18` }}>
                    <Icon className="w-6 h-6" style={{ color: c.hex }} />
                  </div>
                  <div className="font-display text-xl text-white mt-6 tracking-tight">{c.t}</div>
                  <p className="text-white/65 text-sm leading-relaxed mt-3">{c.d}</p>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12 rounded-2xl border-l-4 border-accent bg-white/5 p-6 md:p-8 max-w-4xl">
              <p className="text-white/80 leading-relaxed">
                A Universe with a beginning, governed by laws of breathtaking mathematical elegance, expanding by design — this is why we teach science not as a collection of facts to memorise, but as the ongoing discovery of <span className="text-white font-medium">a rational, ordered creation</span>. It is the conviction behind our name: <span className="text-accent font-semibold">THECREATOR</span>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
